import {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  MoreLinkAction,
  MoreLinkArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { EventDragStartArg } from '@fullcalendar/interaction';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Menu, MenuItem, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBootstrap } from '../../../../../hooks/useBootstrap';
import { useMoveWorkoutDate } from '../../../../../hooks/useMoveWorkoutDate';
import { toast } from '../../../../../hooks/useToast';
import { useFlags } from '../../../../../context/featureFlag';
import { useViewer } from '../../../../../context/viewer';
import { getLocale } from '../../../../../utils/i18n';
import { usePlanContext } from '../../../context/PlanContext';
import { useCalendarContext } from '../../context/CalendarContext';
import type { HubCalendarEvent } from '../../hooks/useCalendarEvents';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { CreateSessionDrawer } from '../CreateSessionDrawer';
import { HubEventDetailModal } from '../HubEventDetailModal';
import { HubEventsUnavailableBanner } from '../HubEventsUnavailableBanner';
import { WorkoutPreviewDrawer } from '../WorkoutPreviewDrawer';
import './calendar-styles.css';
import { resolveTrackEventColors } from './calendar-event-colors';
import { buildCrossTeamCalendarEvents } from './cross-team-calendar-events';
import { HubEventItem } from './HubEventItem';
import {
  MAIN_CALENDAR_EVENT_SOURCES,
  mainCalendarEventsRef,
} from './mainCalendarEventSource';
import {
  getMoreLinkDayDateIso,
  type MoreEventsDayDialogState,
} from './moreEventsDayDialogUtils';
import { MoreEventsDialogHost } from './MoreEventsDialogHost';
import { WorkoutEventItem } from './WorkoutEventItem';
import {
  getWorkoutEventDropDateIso,
  getWorkoutEventDragBlockedReasonKey,
  isWorkoutEventDragEnabled,
  isWorkoutEventStatusDraggable,
} from './workout-event-drag';
import { hubAllDayEventToCalendarDate } from '../hubEventFormatting';

type CalendarGridProps = {
  calendarRef: React.RefObject<FullCalendar>;
  currentDate: DateTime;
  onMoreLinkClick: (info: MoreLinkArg) => void;
  onDatesSet: (arg: DatesSetArg) => void;
  onHubEventClick: (info: EventClickArg) => void;
  renderEventContent: (eventInfo: EventContentArg) => JSX.Element;
  /** Workout drag (SC-5441) — on only while the drag flag is on. */
  editable: boolean;
  onWorkoutEventDragStart: (info: EventDragStartArg) => void;
  onWorkoutEventDrop: (info: EventDropArg) => void;
};

const CalendarGrid = memo(function CalendarGrid({
  calendarRef,
  currentDate,
  onMoreLinkClick,
  onDatesSet,
  onHubEventClick,
  renderEventContent,
  editable,
  onWorkoutEventDragStart,
  onWorkoutEventDrop,
}: CalendarGridProps) {
  return (
    <FullCalendar
      ref={calendarRef}
      plugins={[dayGridPlugin, interactionPlugin]}
      initialView="dayGridMonth"
      initialDate={currentDate.toJSDate()}
      headerToolbar={false}
      dayHeaderFormat={{ weekday: 'short' }}
      fixedWeekCount={false}
      height="100%"
      dayMaxEventRows
      moreLinkClick={onMoreLinkClick}
      datesSet={onDatesSet}
      eventClick={onHubEventClick}
      eventSources={MAIN_CALENDAR_EVENT_SOURCES}
      eventContent={renderEventContent}
      // sortOrder first (active-team/hub 0, cross-team 1), then the
      // FullCalendar default fields so timed hub events stay chronological.
      eventOrder="sortOrder,start,-duration,allDay,title"
      editable={editable}
      // Drag only: `editable` also enables event resizing, which this
      // calendar does not implement (no eventResize handler).
      eventDurationEditable={false}
      eventDragStart={onWorkoutEventDragStart}
      eventDrop={onWorkoutEventDrop}
    />
  );
});

// Anchor position + clicked day for the "Create Session" dropdown.
type SessionMenu = {
  position: { top: number; left: number };
  date: string;
} | null;

export function MainCalendar() {
  const { t } = useTranslation();
  const { dragInteractionsFlag } = useFlags();
  const { hasPermissions } = useViewer();
  const { moveWorkoutDate, isWorkoutMoving } = useMoveWorkoutDate();
  const {
    calendarRef,
    eventsByTrack,
    selectedTrackIds,
    selectedCrossTeamTrackIds,
    currentDate,
    deselectedHubEventTypeIds,
    visibleRange,
    setVisibleRange,
    additionalTeamsData,
  } = useCalendarContext();
  const {
    teamId,
    selectedAthlete,
    filteredTracks,
    tracks,
    showAdditionalTeams,
    showCanceled,
  } = usePlanContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMorePopoverOpenRef = useRef(false);
  const setMoreEventsDialogRef = useRef<
    (state: MoreEventsDayDialogState | null) => void
  >(() => {});
  const onMoreDialogOpenChangeRef = useRef((open: boolean) => {
    isMorePopoverOpenRef.current = open;
  });
  const [selectedHubEvent, setSelectedHubEvent] =
    useState<HubCalendarEvent | null>(null);
  const [sessionMenu, setSessionMenu] = useState<SessionMenu>(null);
  const [sessionDrawerDate, setSessionDrawerDate] = useState<string | null>(
    null,
  );
  const [previewWorkoutSelection, setPreviewWorkoutSelection] = useState<{
    blockId: string;
    workoutId: string;
    sourceTeamId?: string;
  } | null>(null);
  const {
    palette: {
      products: { performance },
    },
  } = useTheme();
  const trackPrimaryColor = performance.primary;
  const trackBackgroundColor = performance.tertiary;

  const tracksById = useMemo(
    () => new Map(tracks.map((t) => [t.id, t])),
    [tracks],
  );

  const calendarSyncGroups = useMemo(
    () => [
      {
        type: 'TeamCoach_Calendar' as const,
        teamId,
        athleteId: selectedAthlete?.id ?? null,
        date: currentDate.startOf('month').toISODate() ?? '',
      },
    ],
    [teamId, selectedAthlete?.id, currentDate],
  );

  useBootstrap(calendarSyncGroups);

  const handleWorkoutEventClick = useCallback(
    (blockId: string, workoutId: string, sourceTeamId?: string) => {
      setPreviewWorkoutSelection({ blockId, workoutId, sourceTeamId });
    },
    [],
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      setVisibleRange({ start: arg.startStr, end: arg.endStr });
      requestAnimationFrame(() => {
        if (!isMorePopoverOpenRef.current) {
          calendarRef.current?.getApi().updateSize();
        }
      });
    },
    [setVisibleRange, calendarRef],
  );

  const { data: hubData, isError: isHubError } = useCalendarEvents({
    startDate: visibleRange?.start ?? null,
    endDate: visibleRange?.end ?? null,
  });

  const hubEventsById = useMemo(
    () => new Map((hubData?.events ?? []).map((event) => [event.id, event])),
    [hubData?.events],
  );

  const openHubEventDetail = useCallback(
    (eventId: string) => {
      const event = hubEventsById.get(eventId);
      if (event) {
        setSelectedHubEvent(event);
      }
    },
    [hubEventsById],
  );

  const openHubEventDetailRef = useRef(openHubEventDetail);
  openHubEventDetailRef.current = openHubEventDetail;

  const handleHubEventClickRef = useRef((info: EventClickArg) => {
    if (info.event.extendedProps['kind'] !== 'hub') {
      return;
    }
    info.jsEvent.preventDefault();
    openHubEventDetailRef.current(info.event.id);
  });

  const handleHubEventClick = useCallback((info: EventClickArg) => {
    handleHubEventClickRef.current(info);
  }, []);

  const hubCalendarEvents = useMemo(() => {
    const events = (hubData?.events ?? []).filter((event) => {
      const typeId = (event['event_type'] as { id?: string } | null | undefined)
        ?.id;
      if (!typeId) {
        return true;
      }
      return !deselectedHubEventTypeIds.has(typeId);
    });

    return events.map((event) => {
      const allDay = event['is_all_day'] === true;
      return {
        id: event.id,
        title: event.title,
        start: allDay
          ? hubAllDayEventToCalendarDate(event.start_date_time, event.timezone)
          : event.start_date_time,
        end: allDay
          ? hubAllDayEventToCalendarDate(event.end_date_time, event.timezone)
          : event.end_date_time,
        allDay,
        // Explicit 0 (not omitted) keeps eventOrder transitive: FullCalendar
        // compares undefined > 1 but 0 == undefined, which would make hub
        // events sort inconsistently against cross-team events (sortOrder 1).
        sortOrder: 0,
        extendedProps: {
          kind: 'hub' as const,
          startDateTime: event.start_date_time,
          timezone: event.timezone,
          isAllDay: allDay,
        },
        display: 'list-item',
        // Hub events never drag: they are not workouts, and the calendar's
        // `editable` flag is calendar-wide (SC-5441 is workout drag only).
        startEditable: false,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        classNames: ['hub-calendar-event'],
      };
    });
  }, [hubData?.events, deselectedHubEventTypeIds]);

  const calendarEvents = useMemo(() => {
    const events: object[] = [];
    const locale = getLocale();
    // Workout drag (SC-5441): a drag may start for every workout while the
    // flag is on and the Blocks Edit permission allows (a coach the builder
    // renders read-only cannot drag) — including workouts whose status
    // blocks the move, so the attempt warns at drag start instead of
    // FullCalendar swallowing it silently. The status itself rides along in
    // `extendedProps.status` for the drag start/drop checks.
    const dragEnabled = isWorkoutEventDragEnabled({
      dragInteractionsFlag,
      canEditBlocks: hasPermissions('plan.blocks', 'edit'),
    });

    for (const trackId of selectedTrackIds) {
      const trackEvents = eventsByTrack.get(trackId);
      if (!trackEvents || trackEvents.length === 0) continue;

      const track = tracksById.get(trackId);
      const { primaryColor, backgroundColor } = track
        ? resolveTrackEventColors(
            track,
            tracksById,
            trackPrimaryColor,
            trackBackgroundColor,
          )
        : {
            primaryColor: trackPrimaryColor,
            backgroundColor: trackBackgroundColor,
          };

      for (const event of trackEvents) {
        events.push({
          id: event.id,
          title: event.workoutName,
          start: event.assignedDate,
          allDay: true,
          // Explicit 0, not omitted — see the hub-event push above for why
          // eventOrder needs it.
          sortOrder: 0,
          startEditable: dragEnabled,
          extendedProps: {
            primaryColor,
            backgroundColor,
            workoutId: event.workoutId,
            blockId: event.blockId,
            trackId: event.trackId,
            assignedDate: event.assignedDate,
            blockStartDate: event.blockStartDate,
            blockEndDate: event.blockEndDate,
            status: event.status,
            accessibleDate: DateTime.fromISO(event.assignedDate, {
              zone: 'utc',
            })
              .setLocale(locale)
              .toLocaleString(DateTime.DATE_MED),
          },
        });
      }
    }

    if (showAdditionalTeams) {
      events.push(
        ...buildCrossTeamCalendarEvents(
          additionalTeamsData,
          new Set(selectedCrossTeamTrackIds),
          showCanceled,
        ),
      );
    }

    events.push(...hubCalendarEvents);

    return events;
  }, [
    eventsByTrack,
    selectedTrackIds,
    tracksById,
    hubCalendarEvents,
    trackPrimaryColor,
    trackBackgroundColor,
    showAdditionalTeams,
    additionalTeamsData,
    selectedCrossTeamTrackIds,
    showCanceled,
    dragInteractionsFlag,
    hasPermissions,
  ]);

  mainCalendarEventsRef.current = calendarEvents;

  const handleMoreLinkClickRef = useRef((info: MoreLinkArg) => {
    const seen = new Set<string>();
    const segments = [...info.allSegs, ...info.hiddenSegs].filter((seg) => {
      if (seen.has(seg.event.id)) {
        return false;
      }
      seen.add(seg.event.id);
      return true;
    });
    setMoreEventsDialogRef.current({
      dateIso: getMoreLinkDayDateIso(info),
      events: segments.map((seg) => ({
        id: seg.event.id,
        title: seg.event.title,
        extendedProps: { ...seg.event.extendedProps },
      })),
    });
    // Return a function instead of 'none' — FullCalendar v6 mishandles 'none' as a
    // view name and calls zoomTo('none'), which dispatches CHANGE_DATE and corrupts
    // internal event state. A function return value hits no branch in FC's handler,
    // so nothing happens.
    return (() => {}) as MoreLinkAction;
  });

  const handleMoreLinkClick = useCallback((info: MoreLinkArg) => {
    return handleMoreLinkClickRef.current(info);
  }, []);

  const handleWorkoutEventClickRef = useRef(handleWorkoutEventClick);
  handleWorkoutEventClickRef.current = handleWorkoutEventClick;

  const renderEventContent = useCallback(
    (eventInfo: EventContentArg) => {
      if (eventInfo.event.extendedProps['kind'] === 'hub') {
        return (
          <HubEventItem
            title={eventInfo.event.title}
            startDateTime={eventInfo.event.extendedProps['startDateTime']}
            timezone={eventInfo.event.extendedProps['timezone']}
            isAllDay={eventInfo.event.extendedProps['isAllDay']}
            onClick={() => openHubEventDetailRef.current(eventInfo.event.id)}
          />
        );
      }
      // Cross-team events carry sourceTeamId (undefined for the active
      // team) — the preview drawer opens read-only for them either way.
      return (
        <WorkoutEventItem
          title={eventInfo.event.title}
          primaryColor={eventInfo.event.extendedProps['primaryColor']}
          backgroundColor={eventInfo.event.extendedProps['backgroundColor']}
          accessibleDate={eventInfo.event.extendedProps['accessibleDate']}
          isMoving={isWorkoutMoving(eventInfo.event.extendedProps['workoutId'])}
          onClick={() =>
            handleWorkoutEventClickRef.current(
              eventInfo.event.extendedProps['blockId'],
              eventInfo.event.extendedProps['workoutId'],
              eventInfo.event.extendedProps['sourceTeamId'],
            )
          }
        />
      );
    },
    [isWorkoutMoving],
  );

  /**
   * Workout drag (SC-5441). A drag can start for a workout whose status the
   * builder renders read-only (e.g. in progress) — this is the warning
   * point, and the message explains why the status blocks the move. The
   * drop handler snaps the workout back without saving.
   */
  const handleWorkoutEventDragStart = useCallback(
    (info: EventDragStartArg) => {
      if (info.event.extendedProps['kind'] === 'hub') return;
      const status = info.event.extendedProps['status'];
      if (typeof status !== 'string') return;
      const reasonKey = getWorkoutEventDragBlockedReasonKey(status);
      if (!reasonKey) return;
      toast({
        title: t(reasonKey),
        severity: 'warning',
      });
    },
    [t],
  );

  /**
   * Workout drag (SC-5441). The event always snaps back to its rendered
   * date: the write is server-side, and the calendar re-renders from synced
   * data when the move (and the published block's automatic re-publish)
   * lands. The shared write path owns the same-date no-op, the block-dates
   * bounds rejection (with its error toast), and the saving state.
   */
  const handleWorkoutEventDrop = useCallback(
    (info: EventDropArg) => {
      // Always revert: hub events are never draggable, and workout events
      // re-render from synced data when the move lands.
      info.revert();
      if (info.event.extendedProps['kind'] === 'hub') return;

      // A workout whose status the builder renders read-only already
      // warned at drag start: the drop only snaps it back.
      const status = info.event.extendedProps['status'];
      if (typeof status !== 'string' || !isWorkoutEventStatusDraggable(status))
        return;

      const dropDate = getWorkoutEventDropDateIso(info.event.start);
      if (!dropDate) return;

      moveWorkoutDate({
        blockId: info.event.extendedProps['blockId'],
        blockStartDate: info.event.extendedProps['blockStartDate'],
        blockEndDate: info.event.extendedProps['blockEndDate'],
        workoutId: info.event.extendedProps['workoutId'],
        currentAssignedDate: info.event.extendedProps['assignedDate'],
        newAssignedDate: dropDate,
        profileId: selectedAthlete?.id ?? null,
      });
    },
    [moveWorkoutDate, selectedAthlete],
  );

  // The drop handler's identity must stay stable: react-query returns a fresh
  // mutation object every render, and without the ref the grid would re-render
  // (resetOptions) on every parent render. Assigned in a layout effect (not
  // during render) so an abandoned render can never leave the committed
  // calendar calling a handler from a different athlete view.
  const handleWorkoutEventDropRef = useRef(handleWorkoutEventDrop);
  useLayoutEffect(() => {
    handleWorkoutEventDropRef.current = handleWorkoutEventDrop;
  }, [handleWorkoutEventDrop]);

  const handleWorkoutEventDropStable = useCallback((info: EventDropArg) => {
    handleWorkoutEventDropRef.current(info);
  }, []);

  useLayoutEffect(() => {
    calendarRef.current?.getApi().refetchEvents();
  }, [calendarEvents, calendarRef]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => {
      if (isMorePopoverOpenRef.current) {
        return;
      }

      calendarRef.current?.getApi().updateSize();
    };

    updateSize();

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [calendarRef, currentDate]);

  const handleMoreDialogClose = useCallback(() => {
    requestAnimationFrame(() => {
      calendarRef.current?.getApi().updateSize();
    });
  }, [calendarRef]);

  // Registered on document (not the calendar div) so the handler fires even
  // when an MUI portal overlay is on top; elementsFromPoint pierces the overlay.
  useEffect(() => {
    if (!selectedAthlete) return;

    const onContextMenu = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      // Topmost calendar element under the cursor. elementsFromPoint pierces any
      // overlay on top (open Menu/drawer backdrops are portals outside the
      // container), and find() skips them to the first element we actually own.
      const hit = document
        .elementsFromPoint(e.clientX, e.clientY)
        .find((el) => container.contains(el));
      if (!hit) return;

      // Drawer open: right-click closes it instead of opening a new menu.
      if (sessionDrawerDate) {
        e.preventDefault();
        setSessionDrawerDate(null);
        return;
      }

      // Ignore right-clicks on existing events or the "+N more" link.
      if (hit.closest('.fc-event, .fc-daygrid-more-link')) return;

      const date = hit.closest('.fc-daygrid-day')?.getAttribute('data-date');
      if (!date) return;

      e.preventDefault();
      setSessionMenu({ position: { top: e.clientY, left: e.clientX }, date });
    };

    document.addEventListener('contextmenu', onContextMenu);
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, [selectedAthlete, sessionDrawerDate]);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <HubEventsUnavailableBanner isError={isHubError} />
      <div
        ref={containerRef}
        className="relative flex-1 min-h-0 overflow-hidden calendar-container"
      >
        <CalendarGrid
          calendarRef={calendarRef}
          currentDate={currentDate}
          onMoreLinkClick={handleMoreLinkClick}
          onDatesSet={handleDatesSet}
          onHubEventClick={handleHubEventClick}
          renderEventContent={renderEventContent}
          editable={dragInteractionsFlag}
          onWorkoutEventDragStart={handleWorkoutEventDragStart}
          onWorkoutEventDrop={handleWorkoutEventDropStable}
        />
        <MoreEventsDialogHost
          setDialogStateRef={setMoreEventsDialogRef}
          onOpenChangeRef={onMoreDialogOpenChangeRef}
          onClose={handleMoreDialogClose}
          onHubEventClick={openHubEventDetail}
          onWorkoutEventClick={handleWorkoutEventClick}
        />
      </div>

      <HubEventDetailModal
        event={selectedHubEvent}
        onClose={() => setSelectedHubEvent(null)}
      />

      <Menu
        open={Boolean(sessionMenu)}
        onClose={() => setSessionMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={sessionMenu?.position}
        autoFocus={false}
      >
        <MenuItem
          onClick={() => {
            if (sessionMenu) {
              setSessionDrawerDate(sessionMenu.date);
            }
            setSessionMenu(null);
          }}
        >
          {t('createSession')}
        </MenuItem>
      </Menu>

      <CreateSessionDrawer
        open={Boolean(sessionDrawerDate)}
        athlete={selectedAthlete}
        startDate={sessionDrawerDate}
        onClose={() => setSessionDrawerDate(null)}
      />

      <WorkoutPreviewDrawer
        open={previewWorkoutSelection !== null}
        blockId={previewWorkoutSelection?.blockId ?? null}
        workoutId={previewWorkoutSelection?.workoutId ?? null}
        profileId={selectedAthlete?.id ?? null}
        sourceTeamId={previewWorkoutSelection?.sourceTeamId ?? null}
        onClose={() => setPreviewWorkoutSelection(null)}
      />
    </div>
  );
}
